import { describeMeetingEvent, meetingEventTechnicalLine } from "@/lib/agent/genieMeetings";
import { GenieMeetingEvent } from "@/types/agent/genie-meeting";

interface MeetingActivityProps {
  events: GenieMeetingEvent[];
  genieName: string;
}

const DANGER = "#DC2626";

const formatEventTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });

/**
 * Everything that happened to the meeting, oldest first: our own steps and each
 * notification from the notetaker. The small grey line under each step keeps
 * the raw values, so a stuck or failed meeting can be traced without the
 * notetaker id. Admins, the only ones sent metadata, can also open each step's
 * raw Nylas data (request ids, webhook ids, notetaker snapshots).
 */
export const MeetingActivity = ({ events, genieName }: MeetingActivityProps) => {
  if (events.length === 0) return null;

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity</h3>
      <ol className="relative space-y-4 border-l border-gray-200 ml-1.5">
        {events.map((event) => {
          const { label, reason, tone } = describeMeetingEvent(event, genieName);
          const isError = tone === "error";

          return (
            <li key={event.id} className="pl-5 relative">
              <span
                className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white"
                style={{ backgroundColor: isError ? DANGER : "#4E9CFF" }}
                aria-hidden
              />
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-xs text-gray-500 tabular-nums">{formatEventTime(event.created_at)}</span>
                <span className="text-sm font-medium" style={{ color: isError ? DANGER : "#111827" }}>
                  {label}
                </span>
              </div>
              {reason && <p className="text-sm text-gray-600 mt-0.5">{reason}</p>}
              <p className="text-xs text-gray-400 mt-0.5 break-all font-mono">{meetingEventTechnicalLine(event)}</p>
              {event.metadata && (
                <details className="mt-0.5 group">
                  <summary className="inline-flex items-center min-h-[44px] cursor-pointer text-xs text-gray-500 hover:text-genie-primary select-none">
                    <span className="group-open:hidden">Show raw data</span>
                    <span className="hidden group-open:inline">Hide raw data</span>
                  </summary>
                  <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 border border-gray-100 p-3 text-[11px] leading-relaxed text-gray-600 font-mono">
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                </details>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};
